import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import {
  useCreateAccessoryGroupMutation,
  useUpdateAccessoryGroupMutation,
} from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableAccessoryGroupRow = {
  id?: number | string;
  description?: string;
  order?: number | string;
};

type AccessoryGroupPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  lang: string;
  mode?: "create" | "edit";
  row?: EditableAccessoryGroupRow | null;
  onSaved?: () => Promise<void> | void;
};

const AccessoryGroupPopup: React.FC<AccessoryGroupPopupProps> = ({
  isOpen,
  onClose,
  request,
  lang,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createAccessoryGroup] = useCreateAccessoryGroupMutation();
  const [updateAccessoryGroup] = useUpdateAccessoryGroupMutation();
  const isEditMode = mode === "edit";

  const { control, reset, getValues } = useCustomForm<{
    description: string;
    order: string;
  }>({
    defaultValues: { description: "", order: "0" },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      description: isOpen ? String(row?.description ?? "") : "",
      order: isOpen ? String(row?.order ?? "0") : "0",
    });
  }, [isOpen, reset, row?.description, row?.order]);

  const handleSave = async () => {
    if (!request) return;

    const rowId = row?.id ?? (row as any)?.key;

    if (isEditMode && rowId == null) return;

    const { description, order } = getValues();
    const parsedOrder = Number.parseInt(String(order), 10);
    const baseUrl = request.url.replace(/\/[^/]+$/, '');
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;
    const payload = {
      id: rowId,
      description,
      order: Number.isNaN(parsedOrder) ? 0 : parsedOrder,
      lang,
    };

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateAccessoryGroup({ url, data: payload }).unwrap();
      } else {
        await createAccessoryGroup({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier groupe" : "Nouveau groupe"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Description"}
                  name="description"
                  control={control}
                  type="text"
                  size="large"
                />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"Order"}
                  name="order"
                  control={control}
                  type="number"
                  size="large"
                  min={0}
                  // disabled={isEditMode}
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default AccessoryGroupPopup;